import React, { useEffect, useLayoutEffect, useState } from 'react';
import throttle from 'lodash.throttle';
import { Header, HeaderTheme } from '../../components/Header';
import { HomeNavTheme, HomeNav, MobileHomeNav } from '../../components/HomeNav';

import useWindowDimensions from '../../utils/WindowDimensions';
import './Home.scss';
import { cleanUrlWithDelay } from '../../utils/cleanUrl';

import { DotIndicator } from '../../components/DotIndicator/DotIndicator';

import { isDefined } from 'utils/isDefined';
import { shouldShowMobileNavigation } from './helpers';
import { APPLICATION_SLIDES, NAVIGATION_SECTIONS, PAGE_NAVIGATION_GROUPS_NAMES } from './navigationConfig';

export type HomeSectionScreenProps = {
  screenId: string;
};

export const Home: React.FC = () => {
  const { width } = useWindowDimensions();
  const [headerTheme, setHeaderTheme] = useState<HeaderTheme>('light');
  const [mobileNavActive, setMobileNavActive] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<number>(0);
  const [navTheme, setNavTheme] = useState<HomeNavTheme>('light');
  const [showNav, setShowNav] = useState<boolean>(false);
  const [showMobileNavigation, setShowMobileNavigation] = useState<boolean>(false);

  useEffect(() => {
    setShowMobileNavigation(shouldShowMobileNavigation(width));
  }, [width]);

  useLayoutEffect(() => {
    const scrollSensitivitySetting = 30; // increase/decrease this number to change sensitivity to events (up = less sensitive; down = more sensitive)
    const slideLockInMilliseconds = 800; // amount of time for which scroll is "locked"
    let touchStartingY = 0;

    const slides = document.querySelectorAll('.home-slide') as NodeListOf<HTMLElement>;

    // set zIndex property in descending order, for paralax to work
    for (let index = 0; index < slides.length; index++) {
      slides[index].style.zIndex = (slides.length + 1 - index).toString();
    }

    let currentSlideNumber = 0;
    let locked = false;

    // set timeout to temporarily block further handling of scrolling events
    function lockTimeout() {
      locked = true;
      setTimeout(function () {
        locked = false;
      }, slideLockInMilliseconds);
      onSlideChanged(currentSlideNumber);
    }

    function moveDown() {
      if (currentSlideNumber >= slides.length - 1) return;

      const previousSlide = slides[currentSlideNumber];
      previousSlide.classList.remove('up-scroll');
      previousSlide.classList.add('down-scroll');
      currentSlideNumber++;
    }

    function moveUp() {
      if (currentSlideNumber <= 0) return;
      currentSlideNumber--;
      const currentSlide = slides[currentSlideNumber];
      currentSlide.classList.remove('down-scroll');
      currentSlide.classList.add('up-scroll');
    }

    function parallaxScroll(evt: WheelEvent) {
      if (locked) return;

      // determine how much scroll has moved
      const { deltaY } = evt;
      const isMovingDown = deltaY > scrollSensitivitySetting && currentSlideNumber !== slides.length - 1;
      const isMovingUp = deltaY < -scrollSensitivitySetting && currentSlideNumber !== 0;

      if (isMovingDown) {
        moveDown();
        lockTimeout();
      }

      if (isMovingUp) {
        moveUp();
        lockTimeout();
      }
    }

    function navigateToSectionHash() {
      const { hash } = window.location;

      if (!hash) return;
      const pageGroupId = hash.replace('#', '');
      const firstSlideOfGroupIndex = APPLICATION_SLIDES.findIndex(
        section => section.pageIdentifier === pageGroupId || section.pageNavigationGroupName === pageGroupId,
      );
      const targetSlideIndex = firstSlideOfGroupIndex === -1 ? 0 : firstSlideOfGroupIndex;
      if (currentSlideNumber !== targetSlideIndex) {
        const moveSlides = targetSlideIndex > currentSlideNumber ? moveDown : moveUp;
        const steps = Math.abs(targetSlideIndex - currentSlideNumber);

        for (let i = 0; i < steps; i++) {
          moveSlides();
        }
        lockTimeout();
      }
      cleanUrlWithDelay();
    }

    function onTouchStart(evt: TouchEvent) {
      touchStartingY = evt?.touches[0]?.pageY;
    }

    function parallaxScrollOnTouch(evt: TouchEvent) {
      if (locked) return;

      const touchDelta = evt?.touches[0]?.pageY - touchStartingY;
      const isMovingDown = touchDelta <= -scrollSensitivitySetting && currentSlideNumber !== slides.length - 1;
      const isMovingUp = touchDelta >= scrollSensitivitySetting && currentSlideNumber !== 0;

      if (isMovingDown) {
        moveDown();
        lockTimeout();
      }

      if (isMovingUp) {
        moveUp();
        lockTimeout();
      }
    }

    const onWheelEventHandler = throttle(parallaxScroll, 60);
    const onTouchStartHandler = throttle(onTouchStart, 60);
    const onTouchMoveHandler = throttle(parallaxScrollOnTouch, 60);

    //Trigger for first load with hash, to scroll to correct section if needed
    navigateToSectionHash();
    window.addEventListener('wheel', onWheelEventHandler, false);
    window.addEventListener('touchstart', onTouchStartHandler, false);
    window.addEventListener('touchmove', onTouchMoveHandler, false);
    window.addEventListener('hashchange', navigateToSectionHash, false);
    return () => {
      window.removeEventListener('hashchange', navigateToSectionHash, false);
      window.removeEventListener('wheel', onWheelEventHandler, false);
      window.removeEventListener('touchstart', onTouchStartHandler, false);
      window.removeEventListener('touchmove', onTouchMoveHandler, false);
    };
  }, []);

  function onSlideChanged(currentSlide: number) {
    const sectionConfig = APPLICATION_SLIDES[currentSlide];
    if (isDefined(sectionConfig)) {
      setHeaderTheme(sectionConfig.getHeaderTheme(window.innerWidth));
      setNavTheme(sectionConfig.navigationTheme);
      setShowNav(!sectionConfig.hideNavigation);
      const activeSectionIndex = NAVIGATION_SECTIONS.findIndex(
        section => section.id === sectionConfig.pageNavigationGroupName,
      );
      if (activeSectionIndex === -1) return;
      setActiveSection(activeSectionIndex);
    }
  }

  function toggleMobileNav() {
    setMobileNavActive(!mobileNavActive);
  }

  function renderNavbar() {
    if (showMobileNavigation) {
      return (
        <div className={`mobile-navbar-container ${mobileNavActive ? 'show' : 'hide'}`}>
          <MobileHomeNav
            theme={headerTheme}
            currentActiveSection={activeSection}
            sections={NAVIGATION_SECTIONS}
            letsTalkScreenId={PAGE_NAVIGATION_GROUPS_NAMES.LETS_TALK}
            onClickAtSection={toggleMobileNav}
          />
        </div>
      );
    } else {
      return (
        <>
          <div className={`navbar-container ${showNav ? 'show' : 'hide'}`}>
            <HomeNav theme={navTheme} currentActiveSection={activeSection} sections={NAVIGATION_SECTIONS} />
          </div>
        </>
      );
    }
  }

  return (
    <div>
      {APPLICATION_SLIDES.map(({ content }, index) => (
        <section className='home-slide' key={index}>
          <div className='content-wrapper'>{content}</div>
        </section>
      ))}
      <Header
        theme={headerTheme}
        showMobileNavigation={showMobileNavigation}
        isMobileNavigationActive={mobileNavActive}
        logoLinkHref={`/#${PAGE_NAVIGATION_GROUPS_NAMES.HOME}`}
        letsTalkLink={{
          url: `/#${PAGE_NAVIGATION_GROUPS_NAMES.LETS_TALK}`,
        }}
        onToggleMobileNavigation={toggleMobileNav}
      />

      {renderNavbar()}
      {showNav && <DotIndicator sections={NAVIGATION_SECTIONS} currentSection={activeSection} />}
    </div>
  );
};
